import { Link } from 'react-router-dom';
import { ArrowRightOnRectangleIcon, ArrowsRightLeftIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { podeGuardar } from '@/features/auth/contas/regrasDasContas';
import { abrirFolhaDeContas } from '@/features/auth/contas/FolhaDeContas';

/**
 * O rodapé de encerrar sessão das barras laterais.
 *
 * ─── POR QUE EXISTE ────────────────────────────────────────────────────────
 *
 * Ele não existia, e isso era defeito e não estética: no celular, dentro de
 * `/teacher`, `/admin` ou `/gestao`, NÃO HAVIA botão de sair. O único no DOM
 * era o da barra do topo do `MainLayout`, que é `hidden md:flex` e não
 * renderiza a 390px; o outro morava na gaveta do `MainLayout`, que o
 * `temGavetaPropria` cala dentro dessas áreas. Medido: 0×0 nas duas, com a
 * gaveta aberta e fechada.
 *
 * No aplicativo é onde pesa mais — não há barra de endereços para escapar
 * digitando `/login`.
 *
 * ─── POR QUE NO RODAPÉ DA BARRA, E NÃO NA GAVETA ───────────────────────────
 *
 * Posto na gaveta, resolveria o celular e deixaria o desktop sem. Posto AQUI,
 * vale para as três superfícies de uma vez — desktop, gaveta da área, gaveta do
 * `MainLayout` —, pelo mesmo motivo que o menu passou a valer: é o mesmo
 * componente montado em três lugares.
 *
 * ─── POR QUE FORA DO QUE ROLA ──────────────────────────────────────────────
 *
 * Quem o monta deve pô-lo como IRMÃO do `<nav>` que rola, com `flex-shrink-0`,
 * nunca como filho. Dentro do `<nav>`, numa lista de vinte e um itens, ele desce
 * com a rolagem e some — que é o achado 96 outra vez, em escala menor.
 *
 * ─── POR QUE AQUI ESTÁ "MINHA CONTA" E NÃO MAIS "EXCLUIR MINHA CONTA" ──────
 *
 * A App Store 5.1.1(v) exige o caminho DENTRO do aplicativo. Exige que ele
 * SEJA ALCANÇÁVEL — não que ele fique em destaque, e muito menos encostado no
 * botão que a pessoa usa todo dia. "Excluir minha conta" ficava logo abaixo do
 * Sair, em vermelho, nas três gavetas: dois alvos de 44px vizinhos, e um erro
 * de dedo entre "volto amanhã" e "não volto mais".
 *
 * O que ficou no lugar não é ausência: é um item "Minha conta", daqui para
 * `/conta`, onde a exclusão mora agora. O caminho continua nas MESMAS três
 * superfícies, pelo mesmo motivo de sempre — é o mesmo componente montado em
 * três lugares —, e ficou um toque mais LONGO, não um toque mais escondido.
 *
 * Trocar este item por nada seria a outra reprovação da mesma diretriz: o
 * caminho tem de existir e tem de ser encontrável. Se um dia alguém quiser
 * limpar este rodapé, é este parágrafo que diz por que não dá.
 */
export const SairDaConta = ({ aoSair }: { aoSair?: () => void }) => {
    const { logout, user } = useAuth();
    // "Trocar de conta" (1.4, 23/10/2026): perto de "Minha conta", só para a equipe e as contas de teste — no app (o cofre)
    // e, desde 24/10/2026, no navegador (as contas lembradas, `FolhaDeContasDoNavegador`).
    const trocar = podeGuardar(user);
    return (
        <div className="flex-shrink-0 border-t border-gray-200">
            <div className="px-2 py-2 space-y-1">
                <Link
                    to="/conta"
                    onClick={aoSair}
                    className="w-full flex items-center px-2 py-3 text-sm min-h-[44px] rounded-md text-gray-700 hover:bg-gray-50"
                >
                    <UserCircleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                    Minha conta
                </Link>
                {trocar && (
                    <button
                        type="button"
                        onClick={() => { aoSair?.(); abrirFolhaDeContas(); }}
                        data-testid="gaveta-trocar-de-conta"
                        className="w-full flex items-center px-2 py-3 text-sm min-h-[44px] rounded-md text-gray-700 hover:bg-gray-50"
                    >
                        <ArrowsRightLeftIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                        Trocar de conta
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => { aoSair?.(); logout(); }}
                    // Cinza como os vizinhos: sair não apaga nada (24/10/2026, design D — o vermelho é só do destrutivo).
                    className="w-full flex items-center px-2 py-3 text-sm min-h-[44px] rounded-md text-gray-700 hover:bg-gray-50"
                >
                    <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                    Sair
                </button>
            </div>
        </div>
    );
};

export default SairDaConta;
